/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { FeatureCollection, GeoJsonProperties, Geometry } from 'geojson';
import type { Feature } from 'ol';
import { unByKey } from 'ol/Observable';
import type { StyleLike } from 'ol/style/Style';
import React from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { DataExplorerButtonConnect } from '@opengeoweb/layer-select';
import type { Source } from '@opengeoweb/shared';
import { usePrevious } from '@opengeoweb/shared';
import type {
  CoreAppStore,
  layerTypes,
  syncGroupsTypes,
} from '@opengeoweb/store';
import {
  genericActions,
  genericSelectors,
  layerActions,
  layerSelectors,
  mapActions,
  mapEnums,
  mapSelectors,
  syncConstants,
  syncGroupsSelectors,
  uiSelectors,
  uiTypes,
  useSetupDialog,
} from '@opengeoweb/store';
import type { Bbox, Dimension, TileServerSettings } from '@opengeoweb/webmap';
import { getWMLayerById } from '@opengeoweb/webmap';
import type {
  MapLocation,
  UpdateLayerInfoPayload,
} from '@opengeoweb/webmap-react';
import {
  genericOpenLayersFeatureStyle,
  getLayerUpdateInfo,
  MapControls,
  OpenLayersFeatureLayer,
  OpenLayersGetFeatureInfo,
  OpenLayersLayer,
  OpenLayersMapView,
  OpenLayersZoomControl,
  TimeContext,
  useSetIntervalWhenVisible,
  WMSLayer,
} from '@opengeoweb/webmap-react';
import { useCoreTranslation } from '../../utils/i18n';
import {
  LayerManagerConnect,
  LayerManagerMapButtonConnect,
} from '../LayerManager';
import { LegendConnect, LegendMapButtonConnect } from '../LegendConnect';
import {
  LocationSearchConnect,
  SearchControlButtonConnect,
} from '../LocationSearch';
import {
  createSelectedFeatureIdsByLayerKey,
  getPanelIdsByFeatureKey,
  createLinkedFeatureCollection,
  createWarningDialogFeatureKeys,
  createWarningDialogFeatureLayers,
  getFeatureSelectionTargetPanelIds,
} from './olMapFeatureSyncUtils';
import { OlMapViewLayerConnect } from './OlMapViewLayerConnect';
import { OpenLayersFeatureLayerConnect } from '../MapDrawToolRedux/OpenLayersFeatureLayerConnect';
import { MultiDimensionSelectMapButtonsConnect } from '../MultiMapDimensionSelectConnect';
import {
  getCenterOfLinkedFeature,
  getIsOutdated,
  useFitAndHighlightWarningFeature,
  usePrefetchTimeSpan,
} from './olMapUtils';
import { useGetOpenLayersView } from './useGetOpenLayersView';
import { useMapViewLoadingIndicator } from './useMapViewLoadingIndicator';
import { ClockConnect } from '../ClockConnect';
import { MapOptionsButtonConnect, GetFeatureInfoConnect } from '../MapOptions';

export interface MapControlsProps {
  mapControlsPositionTop?: number;
  search?: boolean;
  zoomControls?: boolean;
  layerManagerAndLegend?: boolean;
  multiLegend?: boolean;
  dimensionSelect?: boolean;
  getFeatureInfo?: boolean;
  dataExplorerButton?: boolean;
  measureToolButton?: boolean;
  additionalMapControls?: React.ReactNode;
}

export interface OlMapViewConnectProps {
  mapId: string;
  children?: React.ReactNode;
  controls?: MapControlsProps;
  initialBbox?: Bbox;
  holdShiftToScroll?: boolean;
  displayTimeInMap?: boolean;
  showScaleBar?: boolean;
  shouldAutoFetch?: number | boolean; // Auto fetch interval in minutes or boolean
  shouldDisablePrefetching?: boolean;
  tileServerSettings?: TileServerSettings;
  source?: Source;
  featureStyle?: StyleLike;
}

/**
 * Connected component used to display the map and selected layers.
 * Includes options to disable the map controls and legend.
 *
 * Expects the following props:
 * @param {string} mapId mapId: string - Id of the map
 * @param {object} [controls.zoomControls] **optional** controls: object - toggle the map controls, zoomControls defaults to true
 */
const OlMapViewConnect: React.FC<OlMapViewConnectProps> = ({
  mapId,
  children,
  controls = { mapControlsPositionTop: 0, zoomControls: true },
  initialBbox,
  holdShiftToScroll,
  source,
  tileServerSettings,
  featureStyle = genericOpenLayersFeatureStyle,
  displayTimeInMap,
}: OlMapViewConnectProps) => {
  const store = useStore<CoreAppStore>();

  const { view } = useGetOpenLayersView(mapId, store);
  const [hoverLinkedFeature, setHoverLinkedFeature] = React.useState<Feature>();

  const linkedPanelIds = useSelector((store: CoreAppStore) =>
    genericSelectors.selectLinkedPanelIdsForMap(store, mapId),
  );

  const hoveredLinkedFeatureIds = useSelector((store: CoreAppStore) =>
    genericSelectors.selectHoveredFeatureIdsForMap(store, mapId),
  );

  const mapLayerIds = useSelector((store: CoreAppStore) =>
    mapSelectors.getMapLayerIdsEnabled(store, mapId),
  );

  const baseLayers = useSelector((store: CoreAppStore) =>
    mapSelectors.getMapBaseLayers(store, mapId),
  );
  const overLayers = useSelector((store: CoreAppStore) =>
    mapSelectors.getMapOverLayers(store, mapId),
  );
  const layersState = useSelector((store: CoreAppStore) => store.layers);
  const featureLayers = useSelector((store: CoreAppStore) =>
    mapSelectors.getMapFeatureLayers(store, mapId),
  );
  const featureSyncLayerVisibilityById = React.useMemo(() => {
    const visibilityById = new Map<string, boolean>();

    layersState?.allIds?.forEach((layerId) => {
      const layer = layersState.byId[layerId];
      if (!layer?.featureSync) {
        return;
      }

      visibilityById.set(
        layerId,
        layer.enabled !== false && layer.opacity !== 0,
      );
    });

    return visibilityById;
  }, [layersState]);

  const warningDialogFeatureLayers = useSelector((state: CoreAppStore) =>
    genericSelectors.selectFeatureSyncLayersForPanel(state, 'warning-dialog'),
  );
  const warningDialogFeatures = React.useMemo(
    () => createWarningDialogFeatureLayers(warningDialogFeatureLayers),
    [warningDialogFeatureLayers],
  );

  const warningDialogFeatureKeys = React.useMemo(
    () => createWarningDialogFeatureKeys(warningDialogFeatures),
    [warningDialogFeatures],
  );

  // Feature layers (GeoJSON) contributed by all panels linked to this map.
  // Used for rendering selectable/hoverable synced map features.
  const linkedFeaturesFromState = useSelector((store: CoreAppStore) =>
    genericSelectors.selectLinkedFeatureLayersForMap(store, mapId),
  );

  // Form-specific features (draft/edit geometries) from linked panels.
  // Kept separate from linkedFeatures so edit-mode filtering can be applied.
  const linkedFormFeaturesFromState = useSelector((store: CoreAppStore) =>
    genericSelectors.selectLinkedFormFeaturesForMap(store, mapId),
  );
  // Raw shared-data payload grouped per linked panel id.
  // Needed for ownership lookup (feature key -> panel id(s)) when routing
  // selection/hover updates back to the correct source panel(s).
  const linkedFeatureLayersByPanel = useSelector((store: CoreAppStore) =>
    genericSelectors.selectLinkedFeatureLayersByPanelForMap(store, mapId),
  );
  const selectedFeatureIdsByLayerKey = React.useMemo(
    () => createSelectedFeatureIdsByLayerKey(featureLayers),
    [featureLayers],
  );

  const featureOwnerPanelIdsById = React.useMemo(
    () => getPanelIdsByFeatureKey(linkedFeatureLayersByPanel),
    [linkedFeatureLayersByPanel],
  );

  const linkedFeatureCollections = React.useMemo(
    () =>
      linkedFeaturesFromState
        .filter((layer) => {
          const layerId = layer.id;
          if (!layerId) {
            return false;
          }

          const isVisible = featureSyncLayerVisibilityById.get(layerId);
          return isVisible ?? true;
        })
        .map((layer) => ({
          layer,
          featureCollection: createLinkedFeatureCollection(
            [layer],
            warningDialogFeatureKeys,
            selectedFeatureIdsByLayerKey,
          ),
        }))
        .filter(
          ({ featureCollection }) => featureCollection.features.length > 0,
        ),
    [
      featureSyncLayerVisibilityById,
      linkedFeaturesFromState,
      selectedFeatureIdsByLayerKey,
      warningDialogFeatureKeys,
    ],
  );

  const linkedFormFeatures: FeatureCollection = React.useMemo(() => {
    return {
      type: 'FeatureCollection',
      features: linkedFormFeaturesFromState.flatMap((feature) => {
        // Filter out any form features currently in Edit mode
        if (feature.geoJSON.features[0]?.properties?.inEditMode) {
          return [];
        }
        return feature!.geoJSON.features;
      }),
    };
  }, [linkedFormFeaturesFromState]);

  const srs = useSelector((store: CoreAppStore) =>
    mapSelectors.getSrs(store, mapId),
  );

  const timeSliderStartCenterEndTime = useSelector((store: CoreAppStore) =>
    mapSelectors.getMapTimeSliderStartCenterEndAndStep(store, mapId),
  );

  const isTimeScrollingEnabled = useSelector(
    syncGroupsSelectors.isTimeScrollingEnabled,
  );

  const isAnimating = useSelector((store: CoreAppStore) =>
    mapSelectors.linkedMapAnimationInfo(store, mapId),
  )?.isAnimating;

  const autoUpdateLayerId = useSelector((store: CoreAppStore) =>
    mapSelectors.getAutoUpdateLayerId(store, mapId),
  );

  const shouldAutoUpdate = useSelector((store: CoreAppStore) =>
    mapSelectors.isAutoUpdating(store, mapId),
  );

  const autoUpdateLayerMaxValue = useSelector((store: CoreAppStore) =>
    layerSelectors.getLayerTimeDimension(store, autoUpdateLayerId),
  )?.maxValue;

  const autoUpdateTimeStep = useSelector((store: CoreAppStore) =>
    layerSelectors.getTimeStepForLayerId(store, autoUpdateLayerId),
  );

  const dispatch = useDispatch();
  const { t } = useCoreTranslation();

  const updateLayerInformation = React.useCallback(
    (payload: UpdateLayerInfoPayload): void => {
      const updatedPayLoad: UpdateLayerInfoPayload = {
        ...payload,
        origin: 'updateLayerInformation in OlMapViewConnect',
        layerDimensions: {
          layerId: payload.layerDimensions?.layerId || '',
          origin: 'updateLayerInformation in OlMapViewConnect',
          dimensions: (payload.layerDimensions?.dimensions || []).map(
            (layerDim: Dimension) => {
              return { ...layerDim };
            },
          ),
        },
      };

      dispatch(layerActions.onUpdateLayerInformation(updatedPayLoad));
    },
    [dispatch],
  );

  const registerMap = React.useCallback(
    (payload: { mapId: string }): void => {
      dispatch(mapActions.registerMap(payload));
    },
    [dispatch],
  );
  const unregisterMap = React.useCallback(
    (payload: { mapId: string }): void => {
      dispatch(mapActions.unregisterMap(payload));
    },
    [dispatch],
  );

  const syncGroupsAddSource = React.useCallback(
    (payload: syncGroupsTypes.SyncGroupsAddSourcePayload): void => {
      dispatch(genericActions.syncGroupAddSource(payload));
    },
    [dispatch],
  );
  const syncGroupRemoveSource = React.useCallback(
    (payload: syncGroupsTypes.SyncGroupRemoveSourcePayload): void => {
      dispatch(genericActions.syncGroupRemoveSource(payload));
    },
    [dispatch],
  );

  const hoverSelect = React.useMemo(
    () => ({ active: true, onHover: setHoverLinkedFeature }),
    [],
  );

  const displayMapPin = useSelector((store: CoreAppStore) =>
    mapSelectors.getDisplayMapPin(store, mapId),
  );

  const mapPinLocation = useSelector((store: CoreAppStore) =>
    mapSelectors.getPinLocation(store, mapId),
  );

  const forEachOwnedFeatureSyncLayer = React.useCallback(
    (
      state: CoreAppStore,
      panelId: string,
      callback: (layerId: string) => void,
    ): void => {
      const layerIds = genericSelectors.selectFeatureSyncLayerIdsForPanel(
        state,
        panelId,
      );
      layerIds.forEach((layerId) => {
        callback(layerId);
      });
    },
    [],
  );

  const setSelectedFeatureIdsForPanel = React.useCallback(
    (
      state: CoreAppStore,
      panelId: string,
      selectedFeatureIds: string[],
    ): void => {
      forEachOwnedFeatureSyncLayer(state, panelId, (layerId) => {
        dispatch(
          layerActions.setSelectedFeatures({
            layerId,
            selectedFeatureIds,
          }),
        );
      });
    },
    [dispatch, forEachOwnedFeatureSyncLayer],
  );

  const setHoveredFeatureIdsForPanel = React.useCallback(
    (
      state: CoreAppStore,
      panelId: string,
      hoveredFeatureIds: string[],
    ): void => {
      forEachOwnedFeatureSyncLayer(state, panelId, (layerId) => {
        dispatch(
          layerActions.setHoveredFeatures({
            layerId,
            hoveredFeatureIds,
          }),
        );
      });
    },
    [dispatch, forEachOwnedFeatureSyncLayer],
  );

  const disableMapPin = useSelector((store: CoreAppStore) =>
    mapSelectors.getDisableMapPin(store, mapId),
  );

  const mapPinChangeLocation = React.useCallback(
    (clickedLocation: MapLocation): void => {
      if (disableMapPin) {
        return;
      }

      const mapPinLocation =
        getCenterOfLinkedFeature(hoverLinkedFeature, srs) || clickedLocation;

      const selectedFeatureId =
        hoverLinkedFeature?.getId()?.toString() ||
        hoverLinkedFeature?.get('originalId')?.toString() ||
        '';

      const shouldMirrorSelectionToDrawLayer =
        hoverLinkedFeature && hoverLinkedFeature.get('isReadonly') !== true;

      if (shouldMirrorSelectionToDrawLayer) {
        dispatch(
          layerActions.setSelectedFeatures({
            layerId: `drawlayer-${mapId}`,
            selectedFeatureIds: selectedFeatureId ? [selectedFeatureId] : [],
          }),
        );
      }

      const state = store.getState();

      const directlyLinkedPanelIds =
        genericSelectors.selectLinkedPanelIdsForMap(state, mapId);
      const connectedMapIds = new Set<string>([mapId]);
      const connectedPanelIds = new Set<string>();
      const mapsLinked: string[] = [mapId];

      while (mapsLinked.length > 0) {
        const currentMapId = mapsLinked.pop()!;
        const panelIdsForMap = genericSelectors.selectLinkedPanelIdsForMap(
          state,
          currentMapId,
        );

        panelIdsForMap.forEach((panelId) => {
          const isNewPanel = !connectedPanelIds.has(panelId);
          connectedPanelIds.add(panelId);
          if (!isNewPanel) {
            return;
          }

          const mapIdsForPanel = genericSelectors.getLinkedMapIds(
            state,
            panelId,
          );
          mapIdsForPanel.forEach((linkedMapId) => {
            if (!connectedMapIds.has(linkedMapId)) {
              connectedMapIds.add(linkedMapId);
              mapsLinked.push(linkedMapId);
            }
          });
        });
      }

      const directlyLinkedMapIds = new Set<string>([mapId]);
      directlyLinkedPanelIds.forEach((panelId) => {
        genericSelectors
          .getLinkedMapIds(state, panelId)
          .forEach((linkedMapId) => {
            directlyLinkedMapIds.add(linkedMapId);
          });
      });

      const targetPanelIds = getFeatureSelectionTargetPanelIds(
        selectedFeatureId,
        featureOwnerPanelIdsById,
        directlyLinkedPanelIds,
      );

      const mapIdsToUpdate =
        directlyLinkedMapIds.size > 0
          ? Array.from(directlyLinkedMapIds)
          : [mapId];

      if (targetPanelIds.length > 0) {
        directlyLinkedPanelIds.forEach((panelId) => {
          const featureIdForPanel = targetPanelIds.includes(panelId)
            ? selectedFeatureId
            : '';
          setSelectedFeatureIdsForPanel(
            state,
            panelId,
            featureIdForPanel ? [featureIdForPanel] : [],
          );
        });
      } else if (!selectedFeatureId && directlyLinkedPanelIds.length > 0) {
        // In multi-panel feature sync, clicking map space (no hovered feature)
        // should clear stale station selections so each panel can use grid/point.
        directlyLinkedPanelIds.forEach((panelId) => {
          setSelectedFeatureIdsForPanel(state, panelId, []);
        });
      }

      mapIdsToUpdate.forEach((id) => {
        dispatch(mapActions.setMapPinLocation({ mapId: id, mapPinLocation }));
      });
    },
    [
      dispatch,
      featureOwnerPanelIdsById,
      disableMapPin,
      hoverLinkedFeature,
      mapId,
      setSelectedFeatureIdsForPanel,
      srs,
      store,
    ],
  );

  const isActiveWindowId = useSelector((store: CoreAppStore) =>
    uiSelectors.getIsActiveWindowId(store, mapId),
  );

  const animationList = useSelector((store: CoreAppStore) =>
    mapSelectors.getAnimationListAsEpochTimes(store, mapId),
  );

  React.useEffect(() => {
    registerMap({ mapId });
    syncGroupsAddSource({
      id: mapId,
      type: [
        syncConstants.SYNCGROUPS_TYPE_SETTIME,
        syncConstants.SYNCGROUPS_TYPE_SETBBOX,
      ],
    });

    return (): void => {
      unregisterMap({ mapId });
      syncGroupRemoveSource({ id: mapId });
    };
  }, [
    dispatch,
    mapId,
    registerMap,

    syncGroupRemoveSource,
    syncGroupsAddSource,
    unregisterMap,
  ]);

  React.useEffect(() => {
    if (!view) {
      return (): void => {};
    }

    const eventKey = view.on('change', () => {
      const { extent } = view.getViewStateAndExtent();
      dispatch(
        genericActions.setBbox({
          bbox: {
            left: extent[0],
            bottom: extent[1],
            right: extent[2],
            top: extent[3],
          },
          sourceId: mapId,
          srs,
          mapId,
          origin: mapEnums.MapActionOrigin.map,
        }),
      );
    });

    return (): void => {
      unByKey(eventKey);
    };
  }, [view, mapId, srs, dispatch]);

  const featureProperties = React.useMemo(
    () =>
      (hoverLinkedFeature?.getProperties?.() ?? {}) as Record<string, unknown>,
    [hoverLinkedFeature],
  );
  const hoverIdFromFeature = hoverLinkedFeature?.get('originalId');
  const hoverId =
    (typeof hoverIdFromFeature === 'string' && hoverIdFromFeature) ||
    (typeof featureProperties?.originalId === 'string'
      ? featureProperties.originalId
      : undefined);
  const previousHoverId = usePrevious(hoverId);

  React.useEffect(() => {
    if (hoverId === previousHoverId) {
      return;
    }

    if (!hoverLinkedFeature) {
      const state = store.getState();
      linkedPanelIds.forEach((panelId) => {
        setHoveredFeatureIdsForPanel(state, panelId, []);
      });
      return;
    }

    const ownerPanelIds = hoverId
      ? (featureOwnerPanelIdsById[hoverId] ?? [])
      : [];
    const targetPanelIds: string[] = ownerPanelIds;
    if (!targetPanelIds.length) {
      return;
    }

    const strokeOpacity =
      typeof featureProperties?.['stroke-opacity'] === 'number'
        ? featureProperties['stroke-opacity']
        : 0.6;
    const currentOpacity = Math.min(strokeOpacity, 1);
    const increasedOpacity = Math.min(currentOpacity + 0.2, 1);

    hoverLinkedFeature.setProperties({
      ...featureProperties,
      'stroke-opacity': increasedOpacity,
    });

    const state = store.getState();
    linkedPanelIds.forEach((panelId) => {
      setHoveredFeatureIdsForPanel(
        state,
        panelId,
        targetPanelIds.includes(panelId) && hoverId ? [hoverId] : [],
      );
    });
  }, [
    featureOwnerPanelIdsById,
    hoverId,
    previousHoverId,
    hoverLinkedFeature,
    featureProperties,
    linkedPanelIds,
    setHoveredFeatureIdsForPanel,
    store,
  ]);

  const refTimeDimensionModes = useSelector((store: CoreAppStore) =>
    mapSelectors.getRefTimeDimensionModes(store),
  );

  const prefetchTimespan = usePrefetchTimeSpan(
    timeSliderStartCenterEndTime,
    isAnimating,
    animationList,
    refTimeDimensionModes,
  );

  const layerIds = mapLayerIds.join();
  const refetchLayers = React.useCallback(() => {
    mapLayerIds?.forEach(async (layerId) => {
      if (layerId) {
        const wmLayer = getWMLayerById(layerId);
        if (!wmLayer) {
          console.warn('No wmLayer exists for layer id', layerId);
        }
        try {
          await wmLayer.parseLayer(true);
        } catch (e) {
          window.console.error(e);
        } finally {
          const layerInfo = getLayerUpdateInfo(wmLayer, mapId);
          updateLayerInformation?.(layerInfo);
        }
      } else {
        console.warn('Not updating layer, something is wrong.', layerId);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapId, updateLayerInformation, layerIds]);

  useSetIntervalWhenVisible(refetchLayers, 60000);

  /** Check if the autoUpdate layer has outdated data and inform the user by setting the map error */
  const checkIfDataOutdated = React.useCallback(() => {
    if (
      !shouldAutoUpdate ||
      !autoUpdateLayerId ||
      !autoUpdateLayerMaxValue ||
      !autoUpdateTimeStep
    ) {
      dispatch(mapActions.setMapPresetError({ mapId, error: undefined }));
      return;
    }

    // check that the difference is more than 4 timesteps with the current time
    const isOutdated = getIsOutdated(
      autoUpdateLayerMaxValue,
      autoUpdateTimeStep,
      4,
    );

    dispatch(
      mapActions.setMapPresetError({
        mapId,
        error: isOutdated ? t('data-outdated') : undefined,
      }),
    );
  }, [
    autoUpdateLayerId,
    autoUpdateLayerMaxValue,
    autoUpdateTimeStep,
    dispatch,
    mapId,
    shouldAutoUpdate,
    t,
  ]);

  // Check every minute if data is outdated
  useSetIntervalWhenVisible(checkIfDataOutdated, 60000);

  // Recheck if data is outdated if the function is updated
  React.useEffect(() => {
    checkIfDataOutdated();
  }, [checkIfDataOutdated]);

  useMapViewLoadingIndicator(mapId, mapLayerIds);

  const [mapMode, setMapMode] = React.useState({
    debugMode: false,
  });
  React.useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent): void => {
      if (event.isTrusted && document.activeElement === document.body) {
        if (event.key === 'd' || event.key === 'D') {
          setMapMode((prev) => {
            return { ...prev, debugMode: !prev.debugMode };
          });
        }
      }
    };
    document.addEventListener('keyup', handleKeyPress);
    return (): void => {
      document.removeEventListener('keyup', handleKeyPress);
    };
  }, [mapMode]);

  const homeExtent = React.useMemo(
    () =>
      initialBbox && [
        initialBbox.left,
        initialBbox.bottom,
        initialBbox.right,
        initialBbox.top,
      ],
    [initialBbox],
  );

  const dialogType = `${uiTypes.DialogTypes.Search}-${mapId}`;
  const { isDialogOpen: isSearchDialogOpen } = useSetupDialog(
    dialogType as uiTypes.DialogTypes,
  );

  const layerDivStyle = React.useMemo((): React.CSSProperties => {
    const style: React.CSSProperties = {
      position: 'absolute',
      zIndex: 1,
      width: '100%',
    };

    if (isSearchDialogOpen) {
      style.top = `${8 + 40}px`;
    }

    return style;
  }, [isSearchDialogOpen]);

  useFitAndHighlightWarningFeature({ view, warningDialogFeatures, srs, mapId });

  const linkedFeatureCollectionsWithHover: FeatureCollection<
    Geometry,
    GeoJsonProperties
  > = React.useMemo(() => {
    const hoveredFeatureIds = new Set<string>(hoveredLinkedFeatureIds);

    return {
      type: 'FeatureCollection',
      features: linkedFeatureCollections.flatMap(({ featureCollection }) =>
        featureCollection.features.map((feature) => ({
          ...feature,
          properties: {
            ...feature.properties,
            hover:
              (feature.id !== undefined &&
                hoveredFeatureIds.has(String(feature.id))) ||
              (typeof feature.properties?.originalId === 'string' &&
                hoveredFeatureIds.has(feature.properties.originalId)),
          },
        })),
      ),
    };
  }, [hoveredLinkedFeatureIds, linkedFeatureCollections]);

  const filteredLinkedFormFeatures: FeatureCollection<
    Geometry,
    GeoJsonProperties
  > = React.useMemo(
    () => ({
      type: 'FeatureCollection',
      features: linkedFormFeatures.features.filter((feature) => {
        const featureId =
          feature.id !== undefined ? String(feature.id) : undefined;
        const originalId =
          typeof feature.properties?.originalId === 'string'
            ? feature.properties.originalId
            : undefined;

        return !(
          (featureId && warningDialogFeatureKeys.has(featureId)) ||
          (originalId && warningDialogFeatureKeys.has(originalId))
        );
      }),
    }),
    [linkedFormFeatures, warningDialogFeatureKeys],
  );

  const combinedLinkedFeatures: FeatureCollection<Geometry, GeoJsonProperties> =
    React.useMemo(
      () => ({
        type: 'FeatureCollection',
        features: [
          ...linkedFeatureCollectionsWithHover.features,
          ...filteredLinkedFormFeatures.features,
        ],
      }),
      [linkedFeatureCollectionsWithHover, filteredLinkedFormFeatures],
    );

  return (
    <TimeContext.Provider value={prefetchTimespan}>
      <OpenLayersMapView
        holdShiftToScroll={holdShiftToScroll}
        isFocused={isActiveWindowId}
        isMouseWheelZoomEnabled={!isTimeScrollingEnabled}
        mapId={mapId}
        view={view}
      >
        <div style={layerDivStyle}>
          {baseLayers.map((layer: layerTypes.Layer, zIndex) => (
            <OpenLayersLayer
              id={`baselayer-${layer?.id}`}
              key={`baselayer-${layer?.id}`}
              tileServerSettings={tileServerSettings}
              zIndex={baseLayers.length - zIndex + 10}
              {...layer}
            />
          ))}
          {mapLayerIds.map((layerId: string, zIndex) => (
            <OlMapViewLayerConnect
              debugMode={mapMode.debugMode}
              key={`layer-${layerId}`}
              layerId={layerId}
              mapId={mapId}
              zIndex={mapLayerIds.length - zIndex + 1000}
            />
          ))}
          {overLayers.map((layer: layerTypes.Layer, zIndex) => (
            <WMSLayer
              key={`overlayer-${layer?.id}`}
              layerName={layer.name!}
              layerOptions={{ zIndex: overLayers.length - zIndex + 2000 }}
              styleName={layer.style!}
              url={layer.service!}
            />
          ))}
          {featureLayers.map((layer, zIndex) => (
            <OpenLayersFeatureLayerConnect
              hoverSelect={hoverSelect}
              key={`featurelayer-${layer?.id}`}
              layer={layer}
              layerId={layer?.id}
              style={featureStyle}
              zIndex={featureLayers.length - zIndex + 3000}
            />
          ))}
          <OpenLayersFeatureLayer
            featureCollection={combinedLinkedFeatures}
            hoverSelect={hoverSelect}
            style={featureStyle}
            zIndex={4000}
          />

          <OpenLayersGetFeatureInfo
            disableMapPin={disableMapPin}
            displayMapPin={displayMapPin}
            mapPinLocation={mapPinLocation}
            selectLocation={mapPinChangeLocation}
          />
        </div>

        <MapControls
          data-testid="mapControls"
          style={{ top: controls?.mapControlsPositionTop }}
        >
          {!!controls?.search && <SearchControlButtonConnect mapId={mapId} />}
          {!!controls?.zoomControls && (
            <OpenLayersZoomControl homeExtent={homeExtent} />
          )}
          {!!controls?.layerManagerAndLegend && (
            <>
              <LayerManagerMapButtonConnect mapId={mapId} source={source} />
              <LegendMapButtonConnect
                mapId={mapId}
                multiLegend={controls?.multiLegend}
                source={source}
              />
            </>
          )}
          {!!controls?.dataExplorerButton && (
            <DataExplorerButtonConnect mapId={mapId} />
          )}
          {!!controls?.dimensionSelect && (
            <MultiDimensionSelectMapButtonsConnect mapId={mapId} />
          )}
          <MapOptionsButtonConnect
            getFeatureInfo={controls?.getFeatureInfo}
            mapId={mapId}
            measureTool={controls?.measureToolButton}
          />
          {controls?.additionalMapControls}
        </MapControls>
        {!!controls?.multiLegend && (
          <LegendConnect mapId={mapId} multiLegend={controls?.multiLegend} />
        )}
        {!!controls?.search && <LocationSearchConnect mapId={mapId} />}
        {!!controls?.getFeatureInfo && (
          <GetFeatureInfoConnect mapId={mapId} showMapId />
        )}
        <LayerManagerConnect bounds="parent" isDocked mapId={mapId} />
        {!!displayTimeInMap && <ClockConnect mapId={mapId} />}
        {children}
      </OpenLayersMapView>
    </TimeContext.Provider>
  );
};

export default OlMapViewConnect;
